import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertTriangle, Phone, Shield, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const EmergencySOSButton = ({ userType = 'user' }) => {
  const [showEmergencyDialog, setShowEmergencyDialog] = useState(false);
  const [isAlertSent, setIsAlertSent] = useState(false);

  const handleEmergencySOS = () => {
    setShowEmergencyDialog(true);
  };

  const handleConfirmSOS = () => {
    // Mock emergency alert
    console.log('🚨 EMERGENCY SOS ACTIVATED:', {
      userType,
      timestamp: new Date().toISOString(),
      location: { lat: 28.6139, lng: 77.2090 }
    });

    setIsAlertSent(true);

    // Simulate police notification
    setTimeout(() => {
      toast({
        title: 'Emergency Alert Sent',
        description: 'Local police and emergency contacts have been notified. Help is on the way.',
        duration: 10000
      });
    }, 1000);

    // Auto close after 3 seconds
    setTimeout(() => {
      setShowEmergencyDialog(false);
      setTimeout(() => setIsAlertSent(false), 500);
    }, 3000);
  };

  return (
    <>
      {/* SOS Button */}
      <Button
        variant="destructive"
        size="lg"
        onClick={handleEmergencySOS}
        className="bg-red-600 hover:bg-red-700 shadow-lg"
      >
        <Shield className="w-5 h-5 mr-2" />
        Emergency SOS
      </Button>

      {/* Emergency Dialog */}
      <Dialog open={showEmergencyDialog} onOpenChange={setShowEmergencyDialog}>
        <DialogContent className="sm:max-w-md">
          {!isAlertSent ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-6 h-6" />
                  Emergency Alert
                </DialogTitle>
                <DialogDescription>
                  This will immediately alert local police and your emergency contacts with your current location.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2">Alert will include:</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    <li>• Your current GPS location</li>
                    <li>• Your name and phone number</li>
                    <li>• {userType === 'user' ? 'Driver details and vehicle info' : 'Customer details'}</li>
                    <li>• Timestamp</li>
                  </ul>
                </div>

                <p className="text-sm text-muted-foreground">
                  Only use in genuine emergencies. False alerts may result in legal consequences.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowEmergencyDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleConfirmSOS}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Send Alert
                </Button>
              </div>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-green-900 mb-2">
                Alert Sent Successfully!
              </h3>
              <p className="text-muted-foreground mb-4">
                Local authorities have been notified with your location.
              </p>
              <p className="text-sm text-muted-foreground">
                Emergency Response Time: ~5-10 minutes
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmergencySOSButton;
